import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Image as ImageIcon,
  Mail,
  MapPin,
  Phone,
  Search,
  Store,
  UserRound,
  XCircle
} from "lucide-react";
import { GetAllListBrands, GetPendingBrandRequests, ReviewBrandRequest } from "../../common/CommonApi";

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingBrands, setPendingBrands] = useState([]);
  const [activeTab, setActiveTab] = useState("approved");
  const [reviewingId, setReviewingId] = useState(null);
  const [canReviewBrands, setCanReviewBrands] = useState(false);

  const fetchAllbrands = async () => {
    try {
      setLoading(true);
      const res = await GetAllListBrands();
      setBrands(res.data.data || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingBrands = async () => {
    try {
      const res = await GetPendingBrandRequests();
      const allowed = res?.status === 200;
      setCanReviewBrands(allowed);
      setPendingBrands(allowed ? res.data.data || [] : []);
    } catch (error) {
      // The endpoint is intentionally only available to the main admin.
      setCanReviewBrands(false);
      setPendingBrands([]);
    }
  };

  useEffect(() => {
    fetchAllbrands();
    fetchPendingBrands();
  }, []);

  const reviewBrand = async (approvalStatus) => {
    if (!selectedBrand || reviewingId) return;
    const action = approvalStatus === "approved" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${action} ${selectedBrand.brandName}?`)) return;

    setReviewingId(selectedBrand._id);
    try {
      const res = await ReviewBrandRequest(selectedBrand._id, approvalStatus);
      if (res?.status === 200) {
        setSelectedBrand(null);
        setActiveTab("approved");
        await Promise.all([fetchAllbrands(), fetchPendingBrands()]);
      }
    } finally {
      setReviewingId(null);
    }
  };

  const filteredBrands = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const source = activeTab === "pending" ? pendingBrands : brands;
    if (!query) return source;

    return source.filter((brand) =>
      [brand.brandName, brand.brandDescription, brand.userId?.fullname, brand.userId?.companyName]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [activeTab, brands, pendingBrands, searchTerm]);

  const activeBrands = brands.filter((brand) => brand.status).length;
  const formatValue = (value) => value || "Not added";
  const formatDate = (value) => value ? new Date(value).toLocaleString() : "Not added";

  return (
    <Container>
      {!selectedBrand ? (
        <>
          <PageHeader>
            <div>
              <Title>All Brands</Title>
              <Subtitle>View approved brands and review pending brand authorization requests.</Subtitle>
            </div>
          </PageHeader>

          <StatsGrid>
            <StatCard>
              <StatNumber>{brands.length}</StatNumber>
              <StatLabel>Total Brands</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{activeBrands}</StatNumber>
              <StatLabel>Active Brands</StatLabel>
            </StatCard>
            {canReviewBrands && <StatCard>
              <StatNumber>{pendingBrands.length}</StatNumber>
              <StatLabel>Pending Approval</StatLabel>
            </StatCard>}
          </StatsGrid>

          <Toolbar>
            <TabButton type="button" active={activeTab === "approved"} onClick={() => setActiveTab("approved")}>
              Approved Brands ({brands.length})
            </TabButton>
            {canReviewBrands && <TabButton type="button" active={activeTab === "pending"} onClick={() => setActiveTab("pending")}>
              Pending Requests ({pendingBrands.length})
            </TabButton>}
            <SearchBox>
              <Search size={16} />
              <SearchInput
                type="text"
                placeholder={activeTab === "pending" ? "Search pending requests..." : "Search brands, vendors, or companies..."}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </SearchBox>
          </Toolbar>

          {loading && (
            <Loader>
              <img src="/assets/loading.gif" alt="Loading brands" />
            </Loader>
          )}

          {!loading && filteredBrands.length === 0 && (
            <EmptyState>
              <Store size={34} />
              <strong>{activeTab === "pending" ? "No pending brand requests" : "No approved brands found"}</strong>
              <span>{activeTab === "pending" ? "New submissions will appear here for review." : "Try a different brand, vendor, or company search."}</span>
            </EmptyState>
          )}

          {!loading && filteredBrands.length > 0 && (
            <BrandGrid>
              {filteredBrands.map((brand) => (
                <BrandCard key={brand._id} type="button" onClick={() => setSelectedBrand(brand)}>
                  <BrandImageWrap>
                    {brand.brandImage ? (
                      <BrandImage src={brand.brandImage} alt={brand.brandName || "Brand"} />
                    ) : (
                      <ImageFallback>
                        <ImageIcon size={28} />
                      </ImageFallback>
                    )}
                  </BrandImageWrap>

                  <BrandInfo>
                    <BrandTopLine>
                      <BrandName>{formatValue(brand.brandName)}</BrandName>
                      <StatusBadge active={activeTab !== "pending" && brand.status}>
                        {activeTab === "pending" ? "Pending" : brand.status ? "Active" : "Inactive"}
                      </StatusBadge>
                    </BrandTopLine>
                    <BrandDesc>{formatValue(brand.brandDescription)}</BrandDesc>

                    <VendorLine>
                      <UserRound size={14} />
                      {formatValue(brand.userId?.fullname)}
                    </VendorLine>
                  </BrandInfo>

                  <CardAction>
                    View details
                    <ArrowRight size={15} />
                  </CardAction>
                </BrandCard>
              ))}
            </BrandGrid>
          )}
        </>
      ) : (
        <DetailView>
          <BackButton type="button" onClick={() => setSelectedBrand(null)}>
            <ArrowLeft size={16} />
            Back to {activeTab === "pending" ? "Pending Requests" : "Brands"}
          </BackButton>

          <DetailHero>
            <DetailImagePanel>
              {selectedBrand.brandImage ? (
                <DetailImage src={selectedBrand.brandImage} alt={selectedBrand.brandName || "Brand"} />
              ) : (
                <DetailImageFallback>
                  <ImageIcon size={42} />
                </DetailImageFallback>
              )}
            </DetailImagePanel>

            <DetailSummary>
              <StatusBadge active={selectedBrand.approvalStatus !== "pending" && selectedBrand.status}>
                {selectedBrand.approvalStatus === "pending" ? "Pending approval" : selectedBrand.status ? "Active" : "Inactive"}
              </StatusBadge>
              <DetailTitle>{formatValue(selectedBrand.brandName)}</DetailTitle>
              <DetailDescription>{formatValue(selectedBrand.brandDescription)}</DetailDescription>
              <MetaGrid>
                <MetaItem>
                  <MetaLabel>Created At</MetaLabel>
                  <MetaValue>{formatDate(selectedBrand.createdAt)}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Brand ID</MetaLabel>
                  <MetaValue>{formatValue(selectedBrand._id)}</MetaValue>
                </MetaItem>
              </MetaGrid>
            </DetailSummary>
          </DetailHero>

          <SectionGrid>
            <InfoPanel>
              <PanelTitle>Vendor Details</PanelTitle>
              {selectedBrand.userId ? (
                <InfoList>
                  <InfoRow>
                    <UserRound size={17} />
                    <div>
                      <InfoLabel>Name</InfoLabel>
                      <InfoValue>{formatValue(selectedBrand.userId.fullname)}</InfoValue>
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <Mail size={17} />
                    <div>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>{formatValue(selectedBrand.userId.email)}</InfoValue>
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <Phone size={17} />
                    <div>
                      <InfoLabel>Phone</InfoLabel>
                      <InfoValue>{formatValue(selectedBrand.userId.phone)}</InfoValue>
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <Building2 size={17} />
                    <div>
                      <InfoLabel>Company</InfoLabel>
                      <InfoValue>{formatValue(selectedBrand.userId.companyName)}</InfoValue>
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <MapPin size={17} />
                    <div>
                      <InfoLabel>Address</InfoLabel>
                      <InfoValue>{formatValue(selectedBrand.address)}</InfoValue>
                    </div>
                  </InfoRow>
                </InfoList>
              ) : (
                <MissingState>Vendor details are not available for this brand.</MissingState>
              )}
            </InfoPanel>

            <InfoPanel>
              <PanelTitle>Account Timeline</PanelTitle>
              <TimelineItem>
                <TimelineDot />
                <div>
                  <InfoLabel>Brand Created</InfoLabel>
                  <InfoValue>{formatDate(selectedBrand.createdAt)}</InfoValue>
                </div>
              </TimelineItem>
              <TimelineItem>
                <TimelineDot />
                <div>
                  <InfoLabel>Vendor Created</InfoLabel>
                  <InfoValue>{formatDate(selectedBrand.userId?.createdDate)}</InfoValue>
                </div>
              </TimelineItem>
            </InfoPanel>

            <InfoPanel>
              <PanelTitle>Authorization Documents</PanelTitle>
              {selectedBrand.brandAuthorizationDocument ? (
                <AuthorizationImage
                  src={selectedBrand.brandAuthorizationDocument}
                  alt={`${selectedBrand.brandName} trademark licence`}
                />
              ) : (
                <MissingState>Authorization document is not available.</MissingState>
              )}
              {selectedBrand.approvalStatus === "pending" && (
                <ReviewActions>
                  <RejectButton type="button" disabled={reviewingId === selectedBrand._id} onClick={() => reviewBrand("rejected")}>
                    <XCircle size={16} /> Reject
                  </RejectButton>
                  <ApproveButton type="button" disabled={reviewingId === selectedBrand._id} onClick={() => reviewBrand("approved")}>
                    <CheckCircle2 size={16} /> {reviewingId === selectedBrand._id ? "Saving..." : "Approve Brand"}
                  </ApproveButton>
                </ReviewActions>
              )}
            </InfoPanel>
          </SectionGrid>
        </DetailView>
      )}
    </Container>
  );
};

export default BrandsPage;

const Container = styled.div`
  min-height: 100vh;
  padding: 28px;
  background: #f8fafc;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
`;

const StatCard = styled.div`
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
`;

const StatNumber = styled.div`
  color: #30d5c8;
  font-size: 28px;
  font-weight: 900;
`;

const StatLabel = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
`;

const TabButton = styled.button`
  border: 1px solid ${({ active }) => (active ? "#0f766e" : "#cbd5e1")};
  border-radius: 8px;
  padding: 10px 12px;
  background: ${({ active }) => (active ? "#0f766e" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#334155")};
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
`;

const SearchBox = styled.div`
  width: min(100%, 460px);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
`;

const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
`;

const BrandCard = styled.button`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #30d5c8;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
  }
`;

const BrandImageWrap = styled.div`
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const BrandImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 14px;
`;

const ImageFallback = styled.div`
  color: #94a3b8;
`;

const BrandInfo = styled.div`
  flex: 1;
  padding: 16px;
`;

const BrandTopLine = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const BrandName = styled.div`
  min-width: 0;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
`;

const BrandDesc = styled.div`
  display: -webkit-box;
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: ${({ active }) => (active ? "#d4f5e9" : "#fde4e4")};
  color: ${({ active }) => (active ? "#056d3b" : "#a61b1b")};
  font-size: 11px;
  font-weight: 900;
`;

const VendorLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
`;

const CardAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 16px;
  border-top: 1px solid #e2e8f0;
  color: #0f766e;
  font-size: 13px;
  font-weight: 900;
`;

const Loader = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 78px;
    height: 78px;
    object-fit: contain;
  }
`;

const EmptyState = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #64748b;

  strong {
    color: #0f172a;
    font-size: 16px;
  }
`;

const DetailView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const BackButton = styled.button`
  width: fit-content;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const DetailHero = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const DetailImagePanel = styled.div`
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
`;

const DetailImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 300px;
  object-fit: contain;
  padding: 18px;
`;

const AuthorizationImage = styled.img`
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const ReviewActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const ReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const RejectButton = styled(ReviewButton)`
  background: #fee2e2;
  color: #b91c1c;
`;

const ApproveButton = styled(ReviewButton)`
  background: #15803d;
  color: #fff;
`;

const DetailImageFallback = styled.div`
  color: #94a3b8;
`;

const DetailSummary = styled.div`
  min-width: 0;
`;

const DetailTitle = styled.h2`
  margin: 12px 0 8px;
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
`;

const DetailDescription = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  padding: 13px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const MetaLabel = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const MetaValue = styled.div`
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
`;

const PanelTitle = styled.h3`
  margin: 0 0 14px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
`;

const InfoList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f766e;
`;

const InfoLabel = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const MissingState = styled.div`
  padding: 14px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 800;
`;

const TimelineItem = styled.div`
  position: relative;
  display: flex;
  gap: 12px;
  padding: 0 0 18px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  margin-top: 4px;
  border-radius: 999px;
  background: #30d5c8;
  box-shadow: 0 0 0 4px #ccfbf1;
`;
